import {Form} from 'antd';
import ui from '../collectionsUI.module.scss';
import {useState} from 'react';
import {toast} from 'react-toastify';
import {
  UploadDocumentToBucket,
  UploadVideoToBucket,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {useMutation} from '@apollo/client';
import {CREATE_COLLECTION} from 'graphql/mutations/collections';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {onTranslateCollection} from './actions';
import {useNavigate} from 'react-router-dom';
import backIcon from 'images/back.png';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {GeneralInfo} from './CollectionTabs/forms/general';
import {FinancialInfo} from './CollectionTabs/forms/financial';
import {
  toInstrumentsInput,
  validateConstituents,
} from './CollectionTabs/forms/CollectionConstituents';
import {DabaScore} from './CollectionTabs/forms/dabaScore';
import {GET_ALL_COLLECTIONS} from 'graphql/queries/collections';

const WIZARD_STEPS = [
  {key: 'GENERAL', name: 'Identity', hint: 'Name, media, description'},
  {key: 'FINANCIAL', name: 'Market', hint: 'Exchange and constituents'},
  {key: 'DABA_SCORE', name: 'Score & publish', hint: 'Daba score, status'},
];

/* Replaces three unlabelled pill buttons that gave no sense of order or
   progress. A step is reachable only once the ones before it are complete. */
const StepRail = ({activeTab, onTabChange, locked}) => (
  <nav className={ui.steps}>
    {WIZARD_STEPS.map((step, index) => {
      const isActive = activeTab === step.key;
      const isDone =
        WIZARD_STEPS.findIndex(item => item.key === activeTab) > index;
      return (
        <button
          key={step.key}
          type="button"
          className={`${ui.step} ${isActive ? ui.stepActive : ''}`}
          disabled={locked[step.key]}
          onClick={() => onTabChange(step.key)}>
          <span
            className={`${ui.stepMark} ${
              isDone ? ui.stepMarkDone : isActive ? ui.stepMarkActive : ''
            }`}>
            {isDone ? '\u2713' : index + 1}
          </span>
          <span className={ui.stepText}>
            <span className={ui.stepName}>{step.name}</span>
            <span className={ui.stepHint}>{step.hint}</span>
          </span>
        </button>
      );
    })}
  </nav>
);

export const CreateCollection = () => {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [firstForm] = Form.useForm();
  const [secondForm] = Form.useForm();
  const [thirdForm] = Form.useForm();
  const announce = Form.useWatch('announce', thirdForm) === true;
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [secondFormDisabled, setSecondFormDisabled] = useState(true);
  const [thirdFormDisabled, setThirdFormDisabled] = useState(true);
  const [imgUrl, setImgUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [progress, setProgress] = useState(0);
  const [videoUploadLoading, setVideoUploadLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [constituents, setConstituents] = useState([{}]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [reportName, setReportName] = useState('Upload PDF Report');
  const [viewPdf, setViewPdf] = useState('');
  const [formValues, setFormValues] = useState({});
  const [pdfReport, setPdfReport] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] =
    useState('');
  const [translatedCollections, setTranslatedCollections] = useState([]);
  const [allowUsers, setAllowUsers] = useState(true);
  const [tradeable, setTradeable] = useState(true);

  const navigate = useNavigate();

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );

  const handleTranslation = async (name, description, language) => {
    const translateProps = {
      sourceLang: language,
      targetLang: selectedTranslationLanguage,
      targetText1: name,
      targetText2: description,
    };
    try {
      const newValues = [...translatedCollections];

      //Check if translation exists
      if (newValues.length && newValues[0]) {
        // Check if we're trying to translate in the same language
        const languageExists = newValues.some(
          data => data.language === selectedTranslationLanguage,
        );
        if (languageExists) {
          const updateTranslation = await onTranslateCollection(
            translateText,
            translateProps,
          );
          const translationIndex = newValues.findIndex(
            data => data.language === selectedTranslationLanguage,
          );
          newValues[translationIndex].name = updateTranslation.name;
          newValues[translationIndex].description =
            updateTranslation.description;
          newValues[translationIndex].language = updateTranslation.language;
          newValues[translationIndex].videoFile = '';
          newValues[translationIndex].loadingVideoUpload = false;
          newValues[translationIndex].videoProgress = 0;
          newValues[translationIndex].imageUrl = '';
          newValues[translationIndex].previewUrl = '';
          newValues[translationIndex].pdfname = 'Supported formats pdf, docs';
          newValues[translationIndex].viewPdf = null;
          newValues[translationIndex].pdfReport = null;
          newValues[translationIndex].pdfLoading = false;
          setTranslatedCollections(newValues);
        }
      } else {
        const triggerTranslation = await onTranslateCollection(
          translateText,
          translateProps,
        );
        if (!triggerTranslation) {
          return toast.error('Something went wrong when Translating');
        }
        newValues.push(triggerTranslation);
        setTranslatedCollections(newValues);
      }
    } catch (error) {
      toast.error('Something went wrong!');
    }
  };

  const onTabChange = e => {
    setActiveTab(e);
  };

  const handleUploadVideo = async e => {
    const selectedFile = e.target.files[0];
    if (selectedFile.type === 'video/mp4') {
      setVideoUploadLoading(true);
      const response = await UploadVideoToBucket(
        selectedFile,
        'annoucement-images',
        setProgress,
      );
      setVideoUrl(response);
      setVideoUploadLoading(false);
      setProgress(0);
    } else {
      toast.error('Please upload a mp4 video');
    }
  };

  const handlePdfFileChange = async e => {
    const selectedFile = e.target.files[0];
    const fileType = ['application/pdf'];
    setReportName(selectedFile.name);
    if (selectedFile) {
      if (selectedFile && fileType.includes(selectedFile.type)) {
        // Upload file to S3 bucket
        setPdfLoading(true);
        const res = await UploadDocumentToBucket(selectedFile);
        setPdfReport(res);
        setPdfLoading(false);
      } else {
        setPdfReport(null);
        toast.error('Please select a valid pdf file');
      }
    } else {
      toast.error('Select a PDF file');
    }
  };
  const handleDeleteFile = () => {
    setPdfReport(null);
    setReportName('Supported formats pdf, docs');
    setViewPdf(null);
  };

  const handleFirstFormSubmit = values => {
    if (imgUrl === '') {
      toast.error('Banner is required');
    } else if (selectedIcon === null) {
      toast.error('icon is required');
    } else {
      setFormValues({...formValues, ...values});
      setSecondFormDisabled(false);
      onTabChange('FINANCIAL');
    }
  };

  const handleSecondFormSubmit = values => {
    // Every constituent rejection comes back from the resolver as
    // "_schema.Error is not a constructor", so nothing useful can be shown
    // after the fact — the rules have to hold before the request goes out.
    const problems = validateConstituents(constituents, {
      allowRemoval: allowUsers,
      minRetained: values?.minInstrumentCount,
    });
    if (problems.length) return toast.error(problems[0]);

    setFormValues({...formValues, ...values});
    setThirdFormDisabled(false);
    onTabChange('DABA_SCORE');
  };

  const [createCollection, {loading: loadingCreation}] =
    useMutation(CREATE_COLLECTION);

  const handleSave = () => {
    if (loadingCreation) return;
    const values = thirdForm.getFieldsValue();
    createCollection({
      variables: {
        input: {
          // Instrument v2: constituents are PublicEquity instruments. The
          // resolver rejects `companies`, `allowUserRemoveCompany` and
          // `minCompanyCount` outright, so none of them may appear here.
          allowUserRemoveInstrument: allowUsers,
          tradeable,
          bannerUrl: imgUrl,
          ticker: formValues?.ticker,
          instruments: toInstrumentsInput(constituents),
          // Was pinned to XOF, which mislabelled every collection on a venue
          // that does not trade in CFA. Chosen on the Market step and defaulted
          // from the exchange's own base currency.
          currency: formValues?.currency,
          ...(values.dabaScore &&
            values.socialImpact && {
              dabaScore: {
                accelerator: Number(values?.Accelerator),
                analogus: Number(values?.Analogue),
                business: Number(values?.business),
                economicsAndFinance: Number(values?.Economics),
                industry: Number(values?.IndustryScore),
                legalCompliance: Number(values?.Legal),
                market: Number(values?.Market),
                pdfReport: pdfReport,
                returnOutlook: Number(values?.Returns),
                riskChallenges: Number(values?.Risks),
                score: Number(values?.dabaScore),
                socialImpact: Number(values?.Social),
                strategyProduct: Number(values?.Strategy),
                team: Number(values?.Team),
              },
            }),
          description: formValues?.collectionDescription,
          linkedCollectionGroups: formValues?.linkedCollectionGroups,
          // Only meaningful when removal is allowed; the field is hidden
          // otherwise and must not be sent.
          ...(allowUsers &&
            formValues?.minInstrumentCount && {
              minInstrumentCount: Number(formValues.minInstrumentCount),
            }),
          iconUrl: selectedIcon,
          industry: formValues?.industry,
          language: formValues?.language,
          // `bannerUrl` is required by the multilang model even though the
          // schema marks it nullable — a translation without one fails the
          // whole write. Fall back to the base banner.
          multilang: translatedCollections.map(data => ({
            bannerUrl: data.imageUrl || imgUrl,
            description: data.description,
            name: data.name,
            pdfReport: data.pdfReport,
            videoUrl: data.videoFile,
            language: data.language,
          })),
          name: formValues?.collectionName,
          orderType: formValues?.orderType,
          status: values?.status ? 'Active' : 'InActive',
          announce: values?.status === true && values?.announce === true,
          stockExchange: selectedExchange?.split('~')[0],
          ...(videoUrl && {videoUrl: videoUrl}),
        },
      },
      refetchQueries: [
        {query: GET_ALL_COLLECTIONS, variables: {filters: {paginate: true}}},
      ],
    })
      .then(({data: {createCollection}}) => {
        if (createCollection.__typename === 'Error') {
          toast.error(createCollection?.message);
        } else {
          toast.success('Collection created successfully');
          navigate('/dashboard/collections');
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
      });
  };

  return (
    <div className={ui.page}>
      <button
        type="button"
        className={ui.back}
        onClick={() => navigate('/dashboard/collections')}>
        <img src={backIcon} alt="" />
        All collections
      </button>

      <div className={ui.pageHead}>
        <div>
          <h1>New collection</h1>
          <p>
            A collection groups public equities from one exchange into a basket
            an investor buys in a single order.
          </p>
        </div>
      </div>

      <div className={ui.wizard}>
        <StepRail
          activeTab={activeTab}
          onTabChange={onTabChange}
          locked={{
            GENERAL: false,
            FINANCIAL: secondFormDisabled,
            DABA_SCORE: thirdFormDisabled,
          }}
        />
        <div className={ui.wizardBody}>
          {activeTab === 'GENERAL' && (
            <div>
              <GeneralInfo
                firstForm={firstForm}
                handleFirstFormSubmit={handleFirstFormSubmit}
                previewUrl={previewUrl}
                setImgUrl={setImgUrl}
                selectedIcon={selectedIcon}
                handleTranslation={handleTranslation}
                translatedCollections={translatedCollections}
                setTranslatedCollections={setTranslatedCollections}
                setSelectedIcon={setSelectedIcon}
                videoUploadLoading={videoUploadLoading}
                videoUrl={videoUrl}
                setVideoUrl={setVideoUrl}
                progress={progress}
                handleUploadVideo={handleUploadVideo}
                loadingTranslation={loadingTranslation}
                setSelectedTranslationLanguage={setSelectedTranslationLanguage}
                selectedTranslationLanguage={selectedTranslationLanguage}
                setPreviewUrl={setPreviewUrl}
              />
            </div>
          )}
          {activeTab === 'FINANCIAL' && (
            <div>
              <FinancialInfo
                secondForm={secondForm}
                handleSecondFormSubmit={handleSecondFormSubmit}
                constituents={constituents}
                setConstituents={setConstituents}
                selectedExchange={selectedExchange}
                setSelectedExchange={setSelectedExchange}
                allowUsers={allowUsers}
                tradeable={tradeable}
                setTradeable={setTradeable}
                setAllowUsers={setAllowUsers}
                onTabChange={onTabChange}
              />
            </div>
          )}
          {activeTab === 'DABA_SCORE' && (
            <div>
              <DabaScore
                thirdForm={thirdForm}
                setOpenConfirmModal={setOpenConfirmModal}
                handleDeleteFile={handleDeleteFile}
                handlePdfFileChange={handlePdfFileChange}
                onTabChange={onTabChange}
                viewPdf={viewPdf}
                pdfLoading={pdfLoading}
                pdfReport={pdfReport}
                reportName={reportName}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        openModal={openConfirmModal}
        setOpenModal={setOpenConfirmModal}
        title={
          announce ? 'Create and announce collection' : 'Create collection'
        }
        confirmText={
          announce
            ? `Save and announce ${
                formValues?.collectionName || 'this collection'
              } to all users? This sends a push and posts to social channels.`
            : 'Create this collection? It becomes visible to investors as soon as its status is Active.'
        }
        confirmation={true}
        loading={loadingCreation}
        handleOperation={handleSave}
      />
    </div>
  );
};
